/*
 * "Attach as ZIP" - opens a small picker dialog, which compresses the chosen
 * files and streams the finished archive back here to be attached.
 */

/* global Office, window */

import {
  addBase64Attachment,
  clearNotification,
  getComposeItem,
  notifyError,
  notifyInfo,
  notifyProgress,
} from "../common/mailbox";
import { ChildMessage, decode, encode } from "../common/dialogProtocol";
import { MAX_BASE64_LENGTH, base64Bytes, formatBytes, shortenForDisplay } from "../common/zip";
import { language, s as t } from "../common/strings";

const KEY = "attachAsZip";

interface PickedArchive {
  name: string;
  base64: string;
}

export async function attachAsZip(event: Office.AddinCommands.Event): Promise<void> {
  try {
    const item = getComposeItem();
    if (!item) {
      await notifyError(KEY, t().composeOnlyAttach);
      return;
    }

    await clearNotification(KEY);
    const archive = await pickArchive();
    if (!archive) {
      await clearNotification(KEY);
      return;
    }

    if (archive.base64.length > MAX_BASE64_LENGTH) {
      await notifyError(KEY, t().tooLargeToAttach(formatBytes(base64Bytes(archive.base64))));
      return;
    }

    await notifyProgress(KEY, t().attaching(shortenForDisplay(archive.name)));
    await addBase64Attachment(item, archive.base64, archive.name);
    await notifyInfo(
      KEY,
      t().attached(shortenForDisplay(archive.name), formatBytes(base64Bytes(archive.base64)))
    );
  } catch (error) {
    await notifyError(KEY, t().attachFailed((error as Error).message));
  } finally {
    event.completed();
  }
}

/**
 * Shows the picker dialog and reassembles the chunked archive it sends back.
 * Resolves with undefined when the user cancels or closes the dialog.
 */
function pickArchive(): Promise<PickedArchive | undefined> {
  // The dialog has no mailbox context, so the language travels in the URL.
  const dialogUrl = new window.URL(`dialog.html?lang=${language()}`, window.location.href).href;

  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 40, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error.message));
          return;
        }

        const dialog = result.value;
        const chunks: string[] = [];
        let name = `${t().defaultArchiveName}.zip`;
        let expected = 0;
        let received = 0;
        let ackSupported = true;
        let settled = false;

        const settle = (value?: PickedArchive, error?: Error) => {
          if (settled) return;
          settled = true;
          try {
            dialog.close();
          } catch {
            // The dialog may already be gone; nothing to clean up.
          }
          if (error) reject(error);
          else resolve(value);
        };

        const ack = (i: number) => {
          if (!ackSupported) return;
          try {
            dialog.messageChild(encode({ t: "ack", i }));
          } catch {
            ackSupported = false;
          }
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
          const raw = (arg as { message?: string }).message;
          const message = raw ? decode<ChildMessage>(raw) : undefined;
          if (!message) return;

          switch (message.t) {
            case "begin":
              name = message.name;
              expected = message.total;
              chunks.length = 0;
              received = 0;
              notifyProgress(KEY, t().receiving(shortenForDisplay(name)));
              break;
            case "chunk":
              if (chunks[message.i] === undefined) received++;
              chunks[message.i] = message.d;
              ack(message.i);
              break;
            case "end":
              if (received !== expected || chunks.length !== expected) {
                settle(undefined, new Error(t().incompleteTransfer));
              } else {
                settle({ name, base64: chunks.join("") });
              }
              break;
            case "cancel":
              settle(undefined);
              break;
            case "error":
              settle(undefined, new Error(message.message));
              break;
          }
        });

        // Fires when the user closes the dialog with the X, among other things.
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          settle(undefined);
        });
      }
    );
  });
}
